import React,{useEffect,useState} from "react";
import {Link} from "react-router-dom";
import {api} from "@/lib/api";
import {useAuth} from "@/auth";
import {toast} from "sonner";

const NUMBERS=["318","319-320","321-324"];
const TARGETS={
 "318":{name:"PAUL PARAYIL",details:"NEW · HKC · 1 unit · SV"},
 "319-320":{name:"AUTOBAHN",details:"ALLOT · GRC · 2 units · SV"},
 "321-324":{name:"KERALA DEPOT STOCK",details:"ALLOT · ZeCon Reefer · 4 units · LL · STOCK"}
};
const msg=e=>e?.response?.data?.detail||e?.message||"Operation failed";

export default function AdminPcReplacementPage(){
 const {user}=useAuth();
 const [data,setData]=useState(null),[busy,setBusy]=useState(false);
 const [files,setFiles]=useState({}),[approved,setApproved]=useState({});
 const [retirementApproved,setRetirementApproved]=useState(false);
 const [ownerSelections,setOwnerSelections]=useState({});
 const [ownerOverrideConfirmed,setOwnerOverrideConfirmed]=useState(false);
 const [phrase,setPhrase]=useState(""),[error,setError]=useState("");
 const isAdmin=user?.role==="admin";
 const load=async()=>{try{const r=await api.get("/admin/pc-replacement-318-324/overview");setData(r.data);setError("")}
   catch(e){setError(msg(e))}};
 useEffect(()=>{if(isAdmin)load()},[isAdmin]);
 if(!isAdmin)return <div className="p-6 font-medium">Administrator access required.</div>;
 const uploads=new Set((data?.uploads||[]).map(x=>x.number));
 const existingByNumber=Object.fromEntries((data?.records||[]).map(r=>[r.number,r.existing]));
 const complete=data?.release?.status==="complete";
 const retirement=data?.role_records?.find(r=>r.role==="retire_dummy")?.existing;
 const started=Boolean(data?.release);
 const allUploaded=NUMBERS.every(n=>uploads.has(n));
 const allApproved=NUMBERS.every(n=>approved[n]);
 const missingOwners=data?.owner_selection_required||[];
 const ownersSelected=missingOwners.every(n=>Boolean(ownerSelections[n]));
 const allOwnersReady=ownersSelected&&(!missingOwners.length||ownerOverrideConfirmed);
 const canCommit=Boolean(data?.ready && allUploaded && allApproved && allOwnersReady && retirementApproved && retirement?.id && !started && phrase.trim().toUpperCase()==="REPLACE PCS 318-324");
 const stage=async number=>{
    const file=files[number];if(!file){toast.error("Select a Word document for PC "+number);return}
    const body=new FormData();body.append("file",file);
    setBusy(true);
    try{await api.post("/admin/pc-replacement-318-324/stage/"+number,body,
           {headers:{"Content-Type":"multipart/form-data"}});
        toast.success("Verified approved Word document for PC "+number);
        setApproved(prev=>({...prev,[number]:false}));setPhrase("");await load();
    }catch(e){toast.error(msg(e))}finally{setBusy(false)}
 };
 const commit=async()=>{
   if(!canCommit)return;
   if(!window.confirm("Reconcile the four verified existing PC records into the THREE approved Word PCs, and retire only the extra dummy? All four original records are backed up before any changes."))return;
   const expected_ids=Object.fromEntries(NUMBERS.map(n=>[n,existingByNumber[n]?.id]));
   setBusy(true);
   try{const {data:result}=await api.post("/admin/pc-replacement-318-324/commit",
       {confirmation:"REPLACE PCS 318-324",expected_ids,expected_retired_id:retirement.id,
        retirement_confirmed:retirementApproved,owner_selections:ownerSelections,
        owner_override_confirmed:ownerOverrideConfirmed});
       toast.success("PC 318–324 records replaced and verified ("+result.status+")");
       setPhrase("");setApproved({});setRetirementApproved(false);await load();
   }catch(e){toast.error(msg(e));await load()}finally{setBusy(false)}
 };
 const rollback=async()=>{
   if(!window.confirm("Restore the prior PC values from the protected backup? This operation will stop if a PC has been edited or linked since replacement."))return;
   const typed=window.prompt("Type ROLLBACK PCS 318-324 to confirm");
   if(typed!=="ROLLBACK PCS 318-324")return;
   setBusy(true);
   try{await api.post("/admin/pc-replacement-318-324/rollback",{confirmation:typed});
       toast.success("Original PC values restored");await load();
   }catch(e){toast.error(msg(e));await load()}finally{setBusy(false)}
 };
 return <div className="mx-auto max-w-5xl space-y-5 pb-20">
  <div>
   <Link to="/admin" className="text-sm font-semibold text-blue-600">← Back to Admin</Link>
   <h1 className="mt-2 text-3xl font-semibold text-slate-950">Reconcile PCs 318–324 from approved Word files</h1>
   <p className="mt-2 text-sm text-slate-600">One-time Admin-only operation. Preserve all four originals, move Autobahn from 318–319 to 319–320, reuse dummy 320 for Paul Parayil 318, restore archived Kerala Depot Stock from 321–322 to 321–324, and retire the extra dummy 323–324. No other PC numbers will change.</p>
  </div>
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
   <b>Protection:</b> Nothing is overwritten on upload. This operation requires exact identification of all four current records, the three approved Word documents, responsible Sales owners (automatically matched or explicitly selected by Admin), no external links, and explicit confirmation to retire the extra dummy. The rollback snapshot is written before the first PC edit.
  </div>
  {error&&<div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-white p-4">
   <div className="text-sm"><b>Status:</b> {complete?"Replacement completed":started?("Operation "+data.release.status):data?.ready?(missingOwners.length?"Select responsible Sales owners":"Ready for Admin review"):"Preflight blocked or not loaded"}</div>
   <button onClick={load} disabled={busy} className="rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-50">Refresh live record checks</button>
  </div>
  {!!data?.problems?.length&&<section className="rounded-xl border border-red-200 bg-red-50 p-4">
   <b className="text-sm text-red-900">Blocking issues — no PC changes allowed</b>
   <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-800">{data.problems.map((x,i)=><li key={i}>{x}</li>)}</ul>
  </section>}
  {!!data?.conflicting_records?.length&&<section className="rounded-2xl border border-amber-200 bg-white p-5 space-y-3">
   <div><h2 className="text-lg font-semibold text-slate-950">Live records occupying PC numbers 318–324</h2>
    <p className="mt-1 text-sm text-slate-600">Read-only inspection. Every record overlapping the intended PC numbers is listed here, including archived records. These are existing database records, not the uploaded Word documents.</p></div>
   <div className="grid gap-3">
    {data.conflicting_records.map((r,i)=><div key={r.record_id||i} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
       <div className="font-semibold text-slate-950">Stored PC {r.number||"Unknown"} · {r.customer||"Customer not recorded"}</div>
       <span className={"rounded-full px-3 py-1 text-xs font-semibold "+(r.exact_target?.length?"bg-emerald-100 text-emerald-800":"bg-amber-100 text-amber-800")}>{r.exact_target?.length?"Matches target range":"Extra / overlapping range"}</span>
      </div>
      <div className="grid gap-1 text-slate-700 sm:grid-cols-2">
       <div>Actual range: <b>{r.range_start}–{r.range_end}</b> · FY {r.financial_year}</div>
       <div>Type: {r.pc_type||"—"} · Quantity: {r.quantity??"—"}</div>
       <div>Creator: {r.creator||"Not recorded"} · Source: {r.source||"Not recorded"}</div>
       <div>Status: {r.archived?"Archived":"Not archived"} · PC date: {r.pc_date||"—"}</div>
       <div>Container: {r.container_type||"—"}</div>
       <div>Overlaps intended PCs: {(r.overlaps||[]).join(", ")||"—"}</div>
      </div>
      <div className={r.linked_fields?.length?"text-red-700 font-medium":"text-slate-500"}>Related fields: {r.linked_fields?.length?r.linked_fields.join(", "):"None detected on the PC record"}</div>
      <div className="break-all font-mono text-xs text-slate-500">Record ID: {r.record_id||"Missing"}</div>
     </div>)}
   </div>
   <p className="text-xs text-amber-900">No PC records can be replaced until the conflicting number ranges are reconciled and all protection checks pass. This report makes no changes to the database.</p>
  </section>}
  {!started&&missingOwners.length>0&&<section className="rounded-2xl border border-blue-200 bg-blue-50 p-5 space-y-4">
   <div><h2 className="text-lg font-semibold text-blue-950">Assign responsible Sales Staff</h2>
    <p className="mt-1 text-sm text-blue-900">FreshCare cannot uniquely match the name and prefix in the approved Word document to an existing approved Sales account. Select the correct account explicitly. This will NOT alter staff profiles, the document header or the historical creator.</p></div>
   {missingOwners.map(number=><label key={number} className="block space-y-2">
    <span className="block text-sm font-semibold text-slate-900">PC {number} · Document preparer: {number==="321-324"?"Linze (LL)":"Sanjith (SV)"}</span>
    <select value={ownerSelections[number]||""} disabled={busy} onChange={e=>setOwnerSelections(v=>({...v,[number]:e.target.value}))}
     className="w-full rounded-xl border border-blue-200 bg-white px-3 py-3 text-sm text-slate-950">
     <option value="">Select an existing approved Sales Staff account</option>
     {(data?.sales_users||[]).map(u=><option key={u.id} value={u.id}>{u.name||"Unnamed Staff"} · Prefix: {u.prefix||"Not configured"}</option>)}
    </select>
   </label>)}
   {missingOwners.includes("318")&&missingOwners.includes("319-320")&&ownerSelections["318"]&&ownerSelections["319-320"]&&ownerSelections["318"]!==ownerSelections["319-320"]&&
    <p className="text-sm font-semibold text-red-700">Both Sanjith documents must be assigned to the same Sales Staff account.</p>}
   <label className="flex items-start gap-3 text-sm font-medium text-blue-950">
    <input type="checkbox" className="mt-1 h-4 w-4" checked={ownerOverrideConfirmed} disabled={busy||!ownersSelected} onChange={e=>setOwnerOverrideConfirmed(e.target.checked)}/>
    <span>I have checked the selected Sales account(s) against the approved Word documents and authorize assigning responsibility to those accounts, even if their current profile names or prefixes differ. The approved Word document references remain unchanged.</span>
   </label>
  </section>}
  <section className="space-y-3">
   {NUMBERS.map(number=>{
     const row=(data?.records||[]).find(r=>r.number===number);
     const existing=row?.existing;
     const ready=uploads.has(number);
     return <article key={number} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
       <div><h2 className="text-lg font-semibold text-slate-900">PC {number} · {TARGETS[number].name}</h2>
        <p className="text-xs text-slate-500">{TARGETS[number].details}</p></div>
       <span className={"rounded-full border px-3 py-1 text-xs font-semibold "+(ready?"border-green-200 bg-green-50 text-green-700":"border-slate-200 bg-slate-50 text-slate-600")}>
        {ready?"Approved Word staged":"Word not uploaded"}
       </span>
      </div>
      <div className="mt-4 grid gap-3 rounded-xl bg-slate-50 p-3 text-sm sm:grid-cols-2">
       <div><b>Currently in live app</b>
        {existing?<div className="mt-1 text-slate-700">
          <div>Customer: {existing.customer||"—"}</div>
          <div>Quantity: {existing.quantity??"—"} · Type: {existing.type||"—"}</div>
          <div>Creator: {existing.created_by||"—"}</div>
          <div className="mt-1 break-all text-xs text-slate-500">Record ID: {existing.id}</div>
         </div>:<div className="mt-2 text-red-600">No unique existing record identified.</div>}
       </div>
       <div><b>Approved replacement</b><div className="mt-1 text-slate-700">
          <div>Customer: {TARGETS[number].name}</div>
          <div>{TARGETS[number].details}</div>
          <div>Document date: 08/10/2026</div>
          <div className="mt-1 text-xs text-slate-500">Word and PDF will use the original uploaded document.</div>
        </div></div>
      </div>
      {!started&&<div className="mt-4 space-y-2">
        <label className="block text-sm font-medium">Select the approved .docx file for PC {number}</label>
        <div className="flex flex-col gap-2 sm:flex-row">
         <input type="file" accept=".docx" disabled={busy}
          onChange={e=>{setFiles(p=>({...p,[number]:e.target.files?.[0]||null}));setApproved(p=>({...p,[number]:false}))}}
          className="min-w-0 flex-1 rounded-lg border border-slate-200 p-2 text-sm"/>
         <button disabled={!files[number]||busy} onClick={()=>stage(number)}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Verify & stage file</button>
        </div>
       </div>}
      <label className="mt-4 flex items-start gap-3 text-sm text-slate-800">
       <input type="checkbox" checked={Boolean(approved[number])} disabled={busy||started||!ready||!existing||!data?.ready}
        onChange={e=>setApproved(p=>({...p,[number]:e.target.checked}))} className="mt-1 h-4 w-4"/>
       <span>I reviewed the source PC for {number}, and approve its exact correction to match the verified Word document, preserving its original record identity and audit history.</span>
      </label>
     </article>
   })}
  </section>
  {!started&&<section className="rounded-xl border border-amber-200 bg-amber-50 p-5 space-y-3">
    <h2 className="text-lg font-semibold text-amber-950">Retire the extra dummy PC 323–324</h2>
    <p className="text-sm text-amber-900">This existing record is not in the three approved Word files. It will be archived under a non-numeric historical label, not deleted. Its original content remains available in the rollback backup.</p>
    <p className="text-sm text-slate-800"><b>Existing:</b> {retirement?.customer||"Not found"} · {retirement?.quantity??"—"} units · Record ID: {retirement?.id||"Not identified"}</p>
    <label className="flex items-start gap-3 text-sm font-medium text-slate-900">
      <input type="checkbox" className="mt-1 h-4 w-4" checked={retirementApproved} disabled={busy||!data?.ready||!retirement?.id}
        onChange={e=>setRetirementApproved(e.target.checked)}/>
      <span>I confirm this is the extra dummy PC 323–324 and authorize its archival without deleting its original history.</span>
    </label>
  </section>}
  {!started&&<section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
   <h2 className="text-lg font-semibold">Final Admin confirmation</h2>
   <p className="text-sm text-slate-600">All FOUR existing PC records will be backed up before changes. Original creator history is preserved; responsible Sales owners are either automatically matched or selected explicitly by Admin. The approved Word header prefixes, numbering counters and unrelated PCs remain unchanged.</p>
   <label className="block text-sm font-semibold">Type REPLACE PCS 318-324 (not case-sensitive)</label>
   <input value={phrase} onChange={e=>setPhrase(e.target.value)} placeholder="REPLACE PCS 318-324"
     className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm"/>
   <button onClick={commit} disabled={busy||!canCommit}
    className="w-full rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40">
     {busy?"Checking and saving…":"Reconcile four records into the three approved PCs"}
   </button>
  </section>}
  {started&&<section className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
    <b>Operation status: {data.release.status}</b>
    <p className="mt-1 text-slate-600">{complete?"All three source Word files were verified after database save.":"The migration has already started. Repeat submissions are blocked; inspect the recorded status before further action."}</p>
    {(complete||data.release.status==="requires_review")&&
      <button disabled={busy} onClick={rollback} className="mt-3 rounded-xl border border-red-300 px-4 py-2 font-semibold text-red-700 disabled:opacity-40">Rollback using saved originals</button>}
  </section>}
 </div>
}